import React from 'react'

// Animated hero illustration: the lab's digital workflow as a loop. Plain SVG + CSS keyframes
// (see the WORKFLOW banner in src/css/site/site.css) - no animation library, a few KB instead
// of the ~215 kB three.js chunk it replaced.
//
// Geometry lives in one place so the CSS timing can stay simple: a dot orbits the ring once
// per cycle, clockwise from the top, and the steps sit at equal angles starting at the top.
// Each node's highlight is the same keyframe offset by its share of the cycle, so the node
// lights up exactly when the dot arrives. CYCLE must match --nv-wf-cycle in site.css.
const SIZE = 480;
const C = SIZE / 2;
const R = 165;
const CIRC = 2 * Math.PI * R;
const CYCLE = 12; // seconds

const ICONS = {
    scan: (
        <>
            <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" />
            <path d="M4 12h16" />
        </>
    ),
    design: (
        <>
            <rect x="3" y="4" width="18" height="12" rx="2" />
            <path d="M8 20h8M12 16v4" />
            <path d="M8.5 12c1-3 2-4 3.5-4s2.5 1 3.5 4" />
        </>
    ),
    mill: (
        <>
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1L7 17M17 7l2.1-2.1" />
        </>
    ),
    deliver: (
        <>
            <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
            <path d="M3 8l9 5 9-5M12 13v8" />
        </>
    )
};

// Small twinkling points around the ring, purely decorative.
const SPARKS = [[70, 110], [410, 95], [440, 330], [95, 395], [300, 25], [25, 250], [455, 200]];

const Workflow = ({ block }) => {
    const n = block.steps.length;
    const pos = (i) => {
        const a = (-90 + (360 / n) * i) * (Math.PI / 180);
        return [C + R * Math.cos(a), C + R * Math.sin(a)];
    };

    return (
        <svg className="nv-wf" viewBox={`0 0 ${SIZE} ${SIZE}`} role="img"
             aria-label={block.caption.join(' ') + ': ' + block.steps.map((s) => s.label).join(', ')}>
            <defs>
                <radialGradient id="nv-wf-glow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#6946C4" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#6946C4" stopOpacity="0" />
                </radialGradient>
                <linearGradient id="nv-wf-arc" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#A78BFA" />
                    <stop offset="100%" stopColor="#E9E3FF" />
                </linearGradient>
                <linearGradient id="nv-wf-tooth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFFFFF" />
                    <stop offset="100%" stopColor="#C9BCF5" />
                </linearGradient>
                <filter id="nv-wf-blur" x="-100%" y="-100%" width="300%" height="300%">
                    <feGaussianBlur stdDeviation="4" />
                </filter>
            </defs>

            <circle cx={C} cy={C} r={R + 40} fill="url(#nv-wf-glow)" />

            {SPARKS.map(([x, y], i) => (
                <circle key={i} className="nv-wf-spark" cx={x} cy={y} r="2"
                        style={{ animationDelay: `${(i * 0.7) % 3}s` }} />
            ))}

            {/* Track, and the progress arc that fills behind the moving dot. */}
            <circle className="nv-wf-track" cx={C} cy={C} r={R} />
            <circle className="nv-wf-arc" cx={C} cy={C} r={R}
                    strokeDasharray={CIRC} strokeDashoffset={CIRC}
                    transform={`rotate(-90 ${C} ${C})`}
                    style={{ '--nv-wf-circ': CIRC }} />

            {/* Centre: the tooth, gently floating, with the caption under it. */}
            <g className="nv-wf-center">
                {/* Two layers on purpose: the CSS float animation sets `transform`, which
                    replaces an SVG transform attribute on the same element - so the positioning
                    translate lives on the outer group and the animation on the inner one. */}
                <g transform={`translate(${C - 32} ${C - 78})`}>
                    <g className="nv-wf-tooth">
                        <path d="M22 4C13 4 7 10 7 19c0 11 4 17 7 28 2 7 3 17 8 17s5-11 8-17c1-2 3-2 4 0 3 6 3 17 8 17s6-10 8-17c3-11 7-17 7-28 0-9-6-15-15-15-5 0-7 3-10 3S27 4 22 4z"
                              fill="url(#nv-wf-tooth)" />
                        <path d="M16 13c-3 2-4 5-4 9" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
                    </g>
                </g>
                <text className="nv-wf-caption" x={C} y={C + 30} textAnchor="middle">
                    <tspan x={C}>{block.caption[0]}</tspan>
                    <tspan x={C} dy="22">{block.caption[1]}</tspan>
                </text>
            </g>

            {/* Steps. The negative delay puts node i's highlight at i/n of the cycle. */}
            {block.steps.map((s, i) => {
                const [x, y] = pos(i);
                const delay = `${(CYCLE / n) * i - CYCLE}s`;
                return (
                    <g key={s.num}>
                        <circle className="nv-wf-node" cx={x} cy={y} r="34" style={{ animationDelay: delay }} />
                        <svg x={x - 14} y={y - 14} width="28" height="28" viewBox="0 0 24 24"
                             className="nv-wf-icon" style={{ animationDelay: delay }}>
                            {ICONS[s.icon]}
                        </svg>
                        <text className="nv-wf-label" x={x} y={y + 56} textAnchor="middle">
                            <tspan className="nv-wf-num">{s.num} </tspan>{s.label}
                        </text>
                    </g>
                );
            })}

            {/* The travelling dot: a group rotated around the centre, dot parked at the top. */}
            <g className="nv-wf-orbit">
                <circle cx={C} cy={C - R} r="12" fill="#A78BFA" opacity="0.6" filter="url(#nv-wf-blur)" />
                <circle cx={C} cy={C - R} r="6" fill="#FFFFFF" />
            </g>
        </svg>
    );
};

export default Workflow;
